import { format, isAfter, isBefore, isValid, parseISO } from 'date-fns';
import type { ScheduleRuleGet, ScheduleRulePayload } from 'c-sdk';
import type { ScheduleFormDataRule } from 'c-main/Components/Schedule/types';
import { formRuleDataToRule } from 'c-main/Components/Schedule/lib';

/** A rule as the schedule form sends it: `sov` and `slot_length` go as null, never left out. */
export type ScheduleFormRulePayload = Omit<ScheduleRulePayload, 'sov' | 'slot_length'> & {
    sov: string | null;
    slot_length: number | null;
};

/**
 * The form's rules -> the rules the create / update request sends.
 *
 * SOV is sent to 10 decimal places (the API's `decimal` column and its validation regex). On a DSP
 * (insertion-order targeting) campaign SOV and slot length are always sent as null: the API prohibits
 * both, and react-hook-form keeps a value it no longer renders (UL-7912).
 */
export const scheduleFormRulesPayload = (
    rules: ScheduleFormDataRule[],
    isDsp: boolean,
): ScheduleFormRulePayload[] =>
    rules.map(rule => {
        const payload = formRuleDataToRule(rule);

        if (isDsp) return { ...payload, sov: null, slot_length: null };

        return {
            ...payload,
            sov: rule.sov ? parseFloat(rule.sov).toFixed(10) : null,
            slot_length: payload.slot_length ?? null,
        };
    });

/**
 * A date the API sends -> local midnight on the day as written. Takes `2026-10-01`,
 * `2026-10-01T00:00:00+01:00` and `2026-10-01 00:00:00`; the offset is ignored, so the day never
 * moves (and never `new Date('yyyy-MM-dd')`, which is the day before west of UTC).
 */
export const parseScheduleDay = (value: string | null | undefined): Date | null => {
    if (value == null || value.length < 10) return null;
    const day = parseISO(value.slice(0, 10));
    return isValid(day) ? day : null;
};

/** The campaign's booked days, which every rule date has to fall within. Null while unknown. */
export type CampaignDateBounds = { start: Date | null; end: Date | null };

export const campaignDateBounds = (
    dateStart: string | null | undefined,
    dateEnd: string | null | undefined,
): CampaignDateBounds => ({ start: parseScheduleDay(dateStart), end: parseScheduleDay(dateEnd) });

/** The campaign's days as a form rule's `date_range`: a new rule starts out covering the campaign. */
export const campaignDateRange = (bounds: CampaignDateBounds): [string | null, string | null] => [
    bounds.start ? format(bounds.start, 'yyyy-MM-dd') : null,
    bounds.end ? format(bounds.end, 'yyyy-MM-dd') : null,
];

export type RuleDateRangeError = 'endBeforeStart' | 'outsideCampaign';

/**
 * Why a rule's dates can't be saved, or null. The API refuses a rule date before the campaign's
 * `date_start` or after its `date_end` (`ScheduleCreateRequest` / `ScheduleUpdateRequest`). A
 * missing date, or campaign date, is not checked here.
 */
export const ruleDateRangeError = (
    range: readonly [string | null | undefined, string | null | undefined] | null | undefined,
    bounds: CampaignDateBounds,
): RuleDateRangeError | null => {
    const start = parseScheduleDay(range?.[0]);
    const end = parseScheduleDay(range?.[1]);

    if (start && end && isBefore(end, start)) return 'endBeforeStart';

    const outside = [start, end].some(
        day =>
            day != null &&
            ((bounds.start != null && isBefore(day, bounds.start)) ||
                (bounds.end != null && isAfter(day, bounds.end))),
    );
    return outside ? 'outsideCampaign' : null;
};

/** A saved rule that no longer fits the campaign, e.g. after the campaign dates were shortened. */
export const ruleFallsOutsideCampaign = (
    rule: Pick<ScheduleRuleGet, 'start_date' | 'end_date'>,
    bounds: CampaignDateBounds,
): boolean => ruleDateRangeError([rule.start_date, rule.end_date], bounds) === 'outsideCampaign';

/** The "campaign duration" shortcut of the rule date range picker. Invalid dates if unknown. */
export const campaignDurationShortcutRange = (
    dateStart: string | null | undefined,
    dateEnd: string | null | undefined,
): [Date, Date] => [
    parseScheduleDay(dateStart) ?? new Date(NaN),
    parseScheduleDay(dateEnd) ?? new Date(NaN),
];
